import { ChangeDetectorRef, Component, ChangeDetectionStrategy, inject, OnDestroy, OnInit } from "@angular/core";
import { BackendHealthService, ServiceStatus } from "../../_services/backend-health.service";
import { Subscription, interval } from "rxjs";
import { ButtonModule } from "primeng/button";
import { CommonModule } from "@angular/common";
import { environment } from "../../../environments/environment";

/**
 * Component to display backend service status notifications.
 * Shows different messages based on service availability and provides retry functionality.
 *
 * Renders as a plain fixed-position card rather than via PrimeNG's p-toast:
 * p-toast only renders messages added through MessageService, and its custom
 * templates are keyed by "message"/"headless", not arbitrary names like
 * "header"/"content"/"footer" - so driving it directly from this component's
 * own state (as an earlier version of this component tried) never renders
 * anything.
 */
@Component({
  selector: "app-backend-status-notification",
  standalone: true,
  imports: [CommonModule, ButtonModule],
  templateUrl: "./backend-status-notification.component.html",
  styleUrls: ["./backend-status-notification.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BackendStatusNotificationComponent implements OnInit, OnDestroy {
  private backendHealthService = inject(BackendHealthService);
  private cd = inject(ChangeDetectorRef);

  // Reused from the app-wide config rather than hardcoded, so it stays in
  // sync with the same link used elsewhere (e.g. error-card.component.ts).
  readonly contactUrl = environment.contactUrl;

  // Component state
  showNotification = false;
  title = "";
  message = "";
  iconClass = "";
  showActions = false;
  showRetry = false;
  retryCount = 0;
  maxAttempts = 0;
  retryInterval = 0;
  timeUntilRetry = 0;
  // retryCount from BackendHealthService already counts the check that just
  // ran (checkAllHealth() increments it as part of performing the check, so
  // after the very first failed check retryCount is already 1) - it's a
  // 1-indexed "how many attempts have happened" count, not a 0-indexed one,
  // so it's shown directly with no +1 here. displayedAttempt still exists
  // (rather than binding retryCount straight from the template) to keep it
  // capped at maxAttempts as a defensive backstop.
  displayedAttempt = 0;
  maxAttemptsReached = false;

  // Subscriptions
  private statusSubscription: Subscription | undefined;
  // Ticks the "Next retry in Ns" countdown down to 0 once a second while the
  // notification is showing a retryable status. Restarted (back to the full
  // retryInterval) every time a fresh status update comes in, since that's
  // the best available signal that a check just happened - the component
  // has no direct visibility into BackendHealthService's internal timer, so
  // this is an approximation, not a byte-exact countdown to the next tick.
  private countdownSubscription: Subscription | undefined;

  // The status the user last dismissed the notification for, so a dismiss
  // stays hidden while the same ongoing issue keeps getting reported on
  // every background check, but reappears if it changes (e.g. the gateway
  // recovers and then fails again, or backend-unavailable escalates to
  // gateway-unavailable).
  private lastStatus: ServiceStatus = ServiceStatus.AVAILABLE;
  private dismissedForStatus: ServiceStatus | null = null;

  ngOnInit(): void {
    // Subscribe to status changes
    this.statusSubscription = this.backendHealthService
      .getStatusObservable()
      .subscribe((status) => {
        this.updateNotification(status);
      });

    // Initial update
    this.updateNotification(this.backendHealthService.getOverallStatus());
  }

  /**
   * Update notification based on service status
   */
  updateNotification(status: ServiceStatus): void {
    this.lastStatus = status;
    if (status === ServiceStatus.AVAILABLE) {
      this.dismissedForStatus = null;
    }

    this.showNotification =
      status !== ServiceStatus.AVAILABLE && status !== this.dismissedForStatus;
    // This component is OnPush and these fields are set from a manual
    // subscription (not the async pipe), so Angular won't know to re-render
    // without an explicit markForCheck().
    this.cd.markForCheck();

    if (!this.showNotification) {
      this.stopCountdown();
      return;
    }

    // Get current metrics
    this.retryCount = this.backendHealthService.getRetryCount();
    this.maxAttempts = this.backendHealthService.getMaxAttempts();
    this.retryInterval = this.backendHealthService.getRetryInterval();
    this.maxAttemptsReached = this.retryCount >= this.maxAttempts;
    this.displayedAttempt = Math.min(this.retryCount, this.maxAttempts);

    // Update notification based on status
    switch (status) {
      case ServiceStatus.GATEWAY_UNAVAILABLE:
        this.title = "Gateway Service Unavailable";
        this.message =
          "The gateway to our lipids, studies, and datasets appears to be closed for a moment. We'll keep trying to reconnect.";
        this.iconClass = "pi pi-exclamation-circle";
        this.showActions = true;
        // Retry Now just re-runs the same health check the background poll
        // already does automatically - it doesn't restart anything - so it's
        // just as valid here as for the other statuses (e.g. right after an
        // administrator has restarted the gateway).
        this.showRetry = true;
        break;

      case ServiceStatus.BACKEND_UNAVAILABLE:
        this.title = "Backend Service Unavailable";
        // The attempt count and retry countdown are already shown by the
        // "Retry count: N/max" / "Next retry in Ns" lines below the message
        // (see the template) - repeating them here would be redundant.
        this.message = this.maxAttemptsReached
          ? "Our backend still can't locate its lipids, studies, or datasets. Please check back later."
          : "Our backend has misplaced its lipids, studies, and datasets. We're retrying automatically.";
        this.iconClass = "pi pi-exclamation-triangle";
        this.showActions = true;
        this.showRetry = true;
        break;

      case ServiceStatus.PARTIAL_UNAVAILABLE:
        this.title = "Limited Service Availability";
        this.message =
          "A few of our lipid, study, and dataset services are momentarily out of phase. Most functionality should still work.";
        this.iconClass = "pi pi-info-circle";
        this.showActions = true;
        this.showRetry = true;
        break;
    }

    if (this.showRetry) {
      this.restartCountdown();
    } else {
      this.stopCountdown();
    }
  }

  /**
   * (Re)start the "Next retry in Ns" countdown, resetting it to the full
   * retry interval.
   */
  private restartCountdown(): void {
    this.countdownSubscription?.unsubscribe();
    this.timeUntilRetry = Math.round(this.retryInterval / 1000);
    this.countdownSubscription = interval(1000).subscribe(() => {
      this.timeUntilRetry = Math.max(0, this.timeUntilRetry - 1);
      this.cd.markForCheck();
    });
  }

  private stopCountdown(): void {
    this.countdownSubscription?.unsubscribe();
    this.countdownSubscription = undefined;
    this.timeUntilRetry = 0;
  }

  /**
   * Handle retry button click
   */
  onRetry(): void {
    this.backendHealthService.triggerManualRetry();
  }

  /**
   * Handle dismiss button click.
   *
   * Only hides the notification for the current issue - it intentionally
   * does NOT stop background monitoring, since that would silently disable
   * detection of the next outage too.
   */
  onDismiss(): void {
    this.dismissedForStatus = this.lastStatus;
    this.showNotification = false;
    this.stopCountdown();
    this.cd.markForCheck();
  }

  /**
   * Cleanup on destroy
   */
  ngOnDestroy(): void {
    if (this.statusSubscription) {
      this.statusSubscription.unsubscribe();
    }
    this.stopCountdown();
  }
}
